import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { observer } from "mobx-react";
import store from "../Stores/SurveyStore";
import SurveyPage from "../SurveyPage";
import SurveyTitleArea from "../SurveyTitleArea";
import SurveyContentContainer from "../SurveyContentContainer";
import { getPabsLaundryList, getPabsSurveyStatusTypes, saveQuestionsSentDate } from "../../Services/PABS/PabsService";
import { checkIfAnalyst } from "../../Services/PABS/PabsService";
import { changeSurveyCleanerCtrl, changeSurveyStatusCtrl, getSurveyCleanersCtrl } from "../../control";
import { PabsCleaners } from "../../Enums/constants";
import LaundromatTable from "./LaundromatTable";

const getCleanerNameById = (id) => {
    const cleaner = Object.values(PabsCleaners).find(c => c.id === String(id));
    return cleaner?.name || "";
};

const handleStatusUpdate = async (surveyId, statusId) => {
    // Store original values for rollback
    const originalSurvey = store.pabsSurveysList.find(s => s.surveyId === surveyId);
    const originalStatusId = originalSurvey?.surveyStatusTypeId;
    const originalStatusName = originalSurvey?.surveyStatusName;

    // Find the status name for the ID
    const statusType = store.pabsSurveyStatusTypes?.find(st => st.id === statusId);
    const statusName = statusType?.name || "";

    // Optimistically update both fields
    store.updateSurveyField(surveyId, "surveyStatusTypeId", statusId);
    store.updateSurveyField(surveyId, "surveyStatusName", statusName);

    try {
        await changeSurveyStatusCtrl(surveyId, statusId);

        if (statusName === "Done" && !originalSurvey?.dateFirstDone) {
            store.updateSurveyField(surveyId, "dateFirstDone", new Date().toISOString());
        }
    } catch (error) {
        // Rollback to original values
        store.updateSurveyField(surveyId, "surveyStatusTypeId", originalStatusId);
        store.updateSurveyField(surveyId, "surveyStatusName", originalStatusName);
        toast.error("Failed to update status");
    }
};

const handleCleanerUpdate = async (surveyId, cleanerId) => {
    // Store original value for rollback
    const originalSurvey = store.pabsSurveysList.find(s => s.surveyId === surveyId);
    const originalCleaner = originalSurvey?.surveyCleanerID;

    // Optimistically update the cleaner name
    store.updateSurveyField(surveyId, "surveyCleanerID", cleanerId);

    try {
        await changeSurveyCleanerCtrl(surveyId, cleanerId);
        // Success - data is already updated in UI
    } catch (error) {
        store.updateSurveyField(surveyId, "surveyCleanerID", originalCleaner);
        toast.error("Failed to change survey cleaner");
    }
};

const handleDateUpdate = async (surveyId, dateValue) => {
    // Store original value for rollback
    const originalSurvey = store.pabsSurveysList.find(s => s.surveyId === surveyId);
    const originalDate = originalSurvey?.dateQuestionsSent;

    // Convert the date to ISO string format for storage
    const dateToStore = dateValue ? new Date(dateValue + 'T00:00:00').toISOString() : null;

    // Optimistically update the date
    store.updateSurveyField(surveyId, "dateQuestionsSent", dateToStore);

    try {
        await saveQuestionsSentDate(surveyId, dateValue);
        // Success - data is already updated in UI
    } catch (error) {
        // Rollback to original value
        store.updateSurveyField(surveyId, "dateQuestionsSent", originalDate);
        toast.error("Failed to update questions sent date");
    }
};


const PabsLaundromatPage = observer(() => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            if (checkIfAnalyst()) {
                setLoading(true);
                await Promise.all([
                    getSurveyCleanersCtrl(),
                    getPabsLaundryList(),
                    getPabsSurveyStatusTypes()
                ]);
                setLoading(false);
            } else {
                setLoading(false);
            }
        };
        loadData();
    }, []);


    const isAnalyst = checkIfAnalyst();
    
    if (!isAnalyst) {
        return (
            <SurveyPage noFooter={true}>
                <SurveyTitleArea pageName="Laundromat" />
                <SurveyContentContainer>
                    <div style={{textAlign: 'center', paddingTop: '50px'}}>
                        <h3>You are not authorized to view this page.</h3>
                        <p>Only CEM analysts can access the laundromat.</p>
                    </div>
                </SurveyContentContainer>
            </SurveyPage>
        );
    }

    return (
        <SurveyPage noFooter={true}>
            <SurveyTitleArea pageName="Laundromat" />
            <SurveyContentContainer>
                <div>
                    <LaundromatTable 
                        surveyType="PABS"
                        data={store.pabsSurveysList}
                        loading={loading}
                        onStatusChange={(surveyId, statusId) =>
                            handleStatusUpdate(surveyId, statusId)
                        }
                        onCleanerChange={(surveyId, cleanerId) =>
                            handleCleanerUpdate(surveyId, cleanerId)
                        }
                        onQuestionsSentDateChange={(surveyId, dateValue) =>
                            handleDateUpdate(surveyId, dateValue)
                        }

                    />
                </div>
            </SurveyContentContainer>
        </SurveyPage>
    );
});

export default PabsLaundromatPage;